import type { Feature, FeatureCollection, Polygon } from "geojson";
import { batchDeletePolygonTargets, type PolygonDeleteTarget } from "./batchOps";
import { unionPolygons } from "./union";
import { isPolygonValid } from "./validate";
import type { GeometryResult } from "../types";

/**
 * Merge the targeted polygon parts of one layer into a single feature. Parts that overlap or
 * share an edge dissolve into one polygon; parts that don't touch stay separate parts of a
 * MultiPolygon. The merged feature keeps the properties (zone name, …) of the lowest-indexed
 * target and takes its place in the layer; every other feature keeps its order.
 */
export function mergePolygonTargets(
	fc: FeatureCollection,
	targets: PolygonDeleteTarget[],
): GeometryResult<FeatureCollection> {
	if (targets.length < 2) return { ok: false, error: "Select at least two polygons to merge." };

	const parts: Feature<Polygon>[] = [];
	for (const { featureIndex, pathIndex } of targets) {
		const geometry = fc.features[featureIndex]?.geometry;
		const coordinates =
			geometry?.type === "Polygon"
				? geometry.coordinates
				: geometry?.type === "MultiPolygon"
					? geometry.coordinates[pathIndex]
					: undefined;
		if (!coordinates) return { ok: false, error: `No polygon at ${featureIndex}/${pathIndex}.` };
		parts.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates } });
	}

	const union = unionPolygons(parts);
	if (!union.ok) return union;
	const geometry = union.data.geometry;
	if (geometry.type === "MultiPolygon" && geometry.coordinates.length === 0) {
		return { ok: false, error: "Merged polygon is empty, aborting." };
	}
	if (!isPolygonValid(union.data).isValid) {
		return { ok: false, error: "Merged polygon is not valid, aborting." };
	}

	const rest = batchDeletePolygonTargets(fc, targets);
	if (!rest.ok) return rest;

	const first = Math.min(...targets.map((t) => t.featureIndex));
	const merged: Feature = { type: "Feature", properties: { ...fc.features[first].properties }, geometry };
	// The merged feature goes where the first target was. Nothing before the lowest target is removed, so that is
	// still index `first` in what's left.
	const features = [...rest.data.features];
	features.splice(first, 0, merged);
	return { ok: true, data: { type: "FeatureCollection", features } };
}
